import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RAG_META } from "@/lib/rag";
import ScoreGauge from "@/components/ScoreGauge";
import LocalDateTimeField from "@/components/LocalDateTimeField";
import { fmtHM, fmtDate, fmtDT, toLocalInput, todayLocal, yesterdayLocal, fmtDowOrd, tzOffset, zoneShort, isDST } from "@/lib/timefmt";
import { toast } from "sonner";
import { Plus, Trash2, Clock, ChevronLeft, Globe } from "lucide-react";
import EntryCalendar from "@/components/EntryCalendar";
import TimeOnlyField from "@/components/TimeOnlyField";
import MembershipSetup from "@/components/MembershipSetup";

const DAILY_QS = [
  { key: "sleep_24h", label: "Hours of sleep — last 24h" },
  { key: "sleep_48h", label: "Hours of sleep — last 48h" },
  { key: "location_of_rest_daily", label: "Location of your primary rest" },
  { key: "quality_of_rest_daily", label: "Quality of your primary rest" },
  { key: "hours_awake_end_of_duty", label: "Hours awake at END of planned duty" },
  { key: "safety_critical_timing", label: "When will you perform safety-critical tasks?" },
  { key: "alertness", label: "How ALERT are you feeling now?" },
];

export default function DailyEntryPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [optionsByQ, setOptionsByQ] = useState({});
  const [answers, setAnswers] = useState({});
  const [restLocOpts, setRestLocOpts] = useState([]);
  const [restQualOpts, setRestQualOpts] = useState([]);
  const [restPeriods, setRestPeriods] = useState([]);
  const [date, setDate] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const p = q.get("date");
    return !q.get("edit") && p && /^\d{4}-\d{2}-\d{2}$/.test(p) && p <= todayLocal() ? p : todayLocal();
  });
  const [tz, setTz] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [calOpen, setCalOpen] = useState(false);
  const [tzOpen, setTzOpen] = useState(false);
  const [entryDates, setEntryDates] = useState(new Set());
  const [entriesMap, setEntriesMap] = useState({});
  const [editing, setEditing] = useState(false);
  const [dutyStatus, setDutyStatus] = useState(null);
  const [dutyTimes, setDutyTimes] = useState({ start: "", end: "" });
  const [preview, setPreview] = useState({ total: 0, rag: "green" });
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const dateParam = searchParams.get("date");

  useEffect(() => {
    if (editId || !dateParam) return;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateParam) && dateParam <= todayLocal()) setDate(dateParam);
  }, [dateParam, editId]);

  const tzList = useMemo(() => {
    try { return Intl.supportedValuesOf("timeZone"); } catch { return [tz]; }
  }, [tz]);

  const shiftDate = (delta) => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + delta);
    const p = (n) => String(n).padStart(2, "0");
    setDate(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`);
  };

  useEffect(() => {
    api.get("/entries").then((r) => {
      setEntryDates(new Set(r.data.map((e) => e.date)));
      setEntriesMap(Object.fromEntries(r.data.map((e) => [e.date, e])));
    }).catch(() => {});
  }, []);

  const completedEntry = !editId ? entriesMap[date] : null;
  const daysOld = Math.round((new Date(`${todayLocal()}T00:00:00`) - new Date(`${date}T00:00:00`)) / 86400000);
  const canEditCompleted = !!completedEntry && daysOld >= 0 && daysOld <= 7;
  const activeEditId = editId || (editing && completedEntry ? completedEntry.id : null);

  const prefillFrom = (e) => {
    setAnswers(e.answers || {});
    setDate(e.date);
    if (e.timezone) setTz(e.timezone);
    setDutyTimes({ start: e.duty_start || "", end: e.duty_end || "" });
    setRestPeriods((e.rest_periods || []).map((rp) => ({
      _key: rp.id || crypto.randomUUID(),
      start_time: toLocalInput(rp.start_time),
      stop_time: toLocalInput(rp.stop_time),
      location_option_id: rp.location_option_id,
      quality_option_id: rp.quality_option_id,
    })));
  };
  const [qlabels, setQlabels] = useState({});
  const [qrequired, setQrequired] = useState({});
  const [layout, setLayout] = useState([]);
  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/question-labels?company_id=${user.company_id}`).then((r) => {
      setQlabels(r.data.labels || {});
      setQrequired(r.data.required || {});
    }).catch(() => {});
    api.get(`/question-layout?company_id=${user.company_id}`).then((r) => setLayout(r.data.items || [])).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const surveyItems = useMemo(() => {
    if (layout.length) return layout;
    return DAILY_QS.map((q) => ({ id: q.key, kind: "question", key: q.key, label: q.label, required: true }));
  }, [layout]);
  const layoutRequired = useMemo(
    () => Object.fromEntries(surveyItems.filter((i) => i.kind === "question").map((i) => [i.key, i.required !== false])),
    [surveyItems],
  );

  useEffect(() => {
    if (!editId) return;
    api.get("/entries").then((r) => {
      const e = r.data.find((x) => x.id === editId);
      if (!e) { toast.error("Entry not found"); return; }
      prefillFrom(e);
    }).catch(() => {});
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user?.company_id) return;
    setDutyStatus(null);
    let live = true;
    api.get(`/duty/status?date=${date}`).then((r) => {
      if (!live) return;
      setDutyStatus(r.data);
      setDutyTimes({ start: r.data.start || "", end: r.data.end || "" });
    }).catch(() => {});
    return () => { live = false; };
  }, [date, user]);

  useEffect(() => {
    if (!user?.role_id) return;
    api.get(`/options?role_id=${user.role_id}`).then((r) => {
      const grouped = {};
      r.data.forEach((o) => { (grouped[o.question] = grouped[o.question] || []).push(o); });
      setOptionsByQ(grouped);
      setRestLocOpts(grouped["rest_location"] || []);
      setRestQualOpts(grouped["rest_quality"] || []);
    });
  }, [user]);

  useEffect(() => {
    if (!user?.role_id) return;
    api.post("/score/preview", { role_id: user.role_id, answers, rest_periods: restPeriods })
      .then((r) => setPreview({ total: r.data.total, rag: r.data.rag }))
      .catch(() => {});
  }, [answers, restPeriods, user]);

  const setAns = (q, oid) => setAnswers((a) => ({ ...a, [q]: oid }));

  const addRest = () => {
    let start = `${date}T22:00`, stop = `${date}T06:00`;
    if (date === todayLocal()) {
      const now = new Date();
      const s = new Date(now.getTime() - 3600000);
      const p = (n) => String(n).padStart(2, "0");
      start = `${s.getFullYear()}-${p(s.getMonth() + 1)}-${p(s.getDate())}T${p(s.getHours())}:${p(Math.floor(s.getMinutes() / 5) * 5)}`;
      stop = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}T${p(now.getHours())}:${p(Math.floor(now.getMinutes() / 5) * 5)}`;
    }
    setRestPeriods((rs) => [...rs, { _key: crypto.randomUUID(), start_time: start, stop_time: stop, location_option_id: "", quality_option_id: "" }]);
  };
  const updRest = (i, patch) => setRestPeriods((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const delRest = (i) => setRestPeriods((rs) => rs.filter((_, idx) => idx !== i));

  const validIds = useMemo(() => {
    const s = new Set();
    Object.values(optionsByQ).forEach((list) => list.forEach((o) => s.add(o.id)));
    return s;
  }, [optionsByQ]);

  const visibleDaily = surveyItems.filter((q) => q.kind === "question" && (optionsByQ[q.key] || []).length > 0);
  const requiredDaily = visibleDaily.filter((q) => (layout.length ? layoutRequired[q.key] !== false : qrequired[q.key] !== false));
  const histPrefix = date === todayLocal() ? "" : date === yesterdayLocal() ? "For yesterday — " : `For ${fmtDowOrd(date)} — `;
  const answered = visibleDaily.filter((q) => answers[q.key] && validIds.has(answers[q.key])).length;

  const canSubmit = useMemo(
    () => requiredDaily.every((q) => answers[q.key] && validIds.has(answers[q.key]))
      && restPeriods.every((r) => r.start_time && r.stop_time
        && r.location_option_id && validIds.has(r.location_option_id)
        && r.quality_option_id && validIds.has(r.quality_option_id)),
    [answers, restPeriods, optionsByQ, qrequired, validIds], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const submit = async () => {
    if (!canSubmit) { toast.error("Please answer all questions and complete rest periods."); return; }
    setSubmitting(true);
    try {
      const payload = {
        date, timezone: tz, answers,
        duty_start: dutyTimes.start || null,
        duty_end: dutyTimes.end || null,
        rest_periods: restPeriods.map((r) => ({
          start_time: new Date(r.start_time).toISOString(),
          stop_time: new Date(r.stop_time).toISOString(),
          location_option_id: r.location_option_id,
          quality_option_id: r.quality_option_id,
        })),
      };
      if (activeEditId) {
        await api.put(`/entries/${activeEditId}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post("/entries", payload);
        toast.success("Daily entry logged");
      }
      nav("/history");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  if (!user?.company_id || !user?.role_id) {
    return <MembershipSetup title="Fatigue" message="To log your own fatigue entries, join a company and pick your crew role. One-time setup." />;
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{activeEditId ? "Amend Entry" : "Fatigue Check-In"}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Fatigue & Rest Survey</h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button onClick={() => shiftDate(-1)} data-testid="date-prev"
              className="h-9 w-9 grid place-items-center rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors"
              aria-label="Previous day"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setDate(todayLocal())} data-testid="date-today"
              className="px-3 h-9 rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 text-sm transition-colors">Today</button>
            <button onClick={() => setCalOpen((o) => !o)} data-testid="date-picker-btn"
              className="px-3 h-9 rounded-md bg-slate-900 border border-slate-800 hover:border-blue-500 text-sm font-mono-num transition-colors">
              {fmtDate(date)}{completedEntry && !editing ? " (completed)" : ""}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-400 font-mono-num">
            <span data-testid="tz-offset" className="text-slate-300">{tzOffset(tz, date)}</span>
            <span data-testid="tz-short">{zoneShort(tz)}</span>
            {isDST(tz, date) && (
              <span data-testid="tz-dst"
                className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] uppercase tracking-widest">DST</span>
            )}
            <button onClick={() => setTzOpen((o) => !o)} data-testid="tz-change-btn"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-900 border border-slate-800 hover:border-blue-500 text-xs transition-colors">
              <Globe className="h-3 w-3" /> {tz}
            </button>
          </div>
          {calOpen && (
            <div className="mt-3">
              <EntryCalendar value={date} marked={entryDates} onChange={(d) => { setDate(d); setCalOpen(false); }} />
            </div>
          )}
          {tzOpen && (
            <div className="mt-3 w-72 max-h-64 overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 shadow-xl" data-testid="tz-list">
              {tzList.map((z) => (
                <button key={z} ref={z === tz ? (el) => el?.scrollIntoView({ block: "center" }) : undefined}
                  onClick={() => { setTz(z); setTzOpen(false); }} data-testid={`tz-option-${z}`}
                  className={`w-full text-left px-3 py-2 text-xs font-mono-num transition-colors ${
                    z === tz ? "bg-blue-600/15 text-blue-300" : "text-slate-300 hover:bg-slate-800"}`}>
                  {z} <span className="text-slate-500">({tzOffset(z, date)}{isDST(z, date) ? " DST" : ""})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {completedEntry && !editing && !editId ? (
        <CompletedView entry={completedEntry} qlabels={qlabels} canEdit={canEditCompleted} items={surveyItems}
          onEdit={() => { prefillFrom(completedEntry); setEditing(true); }} />
      ) : dutyStatus && (!dutyStatus.on_duty || dutyStatus.off_work) ? (
        <div className="p-6 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="off-duty-notice">
          <div className="font-heading text-xl font-semibold">No survey required</div>
          <p className="text-slate-400 text-sm mt-1">
            You are {dutyStatus.off_work ? "marked Off Work" : "not on duty"} on {fmtDate(date)}.
          </p>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 space-y-5">
          <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="duty-day-card">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">
              Duty Day{dutyStatus?.template ? ` · ${dutyStatus.template}` : ""}
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-sm">
              <TimeOnlyField label="Duty start (local)" testId="duty-start" value={dutyTimes.start} onChange={(v) => setDutyTimes({ ...dutyTimes, start: v })} />
              <TimeOnlyField label="Duty end (local)" testId="duty-end" value={dutyTimes.end} onChange={(v) => setDutyTimes({ ...dutyTimes, end: v })} />
            </div>
          </div>
          {surveyItems.map((it) => {
            if (it.kind === "divider") {
              return (
                <div key={it.id} className="pt-2" data-testid={`divider-${it.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-slate-700" />
                    {it.label && <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">{it.label}</div>}
                    <div className="h-px flex-1 bg-slate-700" />
                  </div>
                </div>
              );
            }
            const q = it;
            const opts = optionsByQ[q.key] || [];
            if (opts.length === 0) return null;
            return (
              <div key={q.key} className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid={`q-${q.key}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Question</div>
                {histPrefix && <div className="text-[11px] uppercase tracking-widest text-amber-400 font-mono-num mb-1" data-testid={`hist-prefix-${q.key}`}>{histPrefix}</div>}
                <div className="font-heading text-lg font-semibold mb-3">
                  {qlabels[q.key] || q.label}
                  {qrequired[q.key] === false && <span className="ml-2 text-[10px] font-mono-num uppercase tracking-widest text-slate-500 align-middle">Optional</span>}
                </div>
                {(
                  <div className="space-y-2">
                    {["green", "yellow", "amber", "red"].map((rag) => {
                      const group = opts.filter((o) => o.rag === rag);
                      if (group.length === 0) return null;
                      return (
                        <div key={rag} className="flex flex-wrap gap-2" data-testid={`q-${q.key}-rag-${rag}`}>
                          {group.map((o) => {
                            const active = answers[q.key] === o.id;
                            const meta = RAG_META[o.rag];
                            return (
                              <button
                                key={o.id}
                                onClick={() => setAns(q.key, o.id)}
                                data-testid={`opt-${q.key}-${o.id}`}
                                className={`px-3 py-2 rounded-md text-sm border transition-all ${meta.cls} ${active ? "ring-2 ring-white/30 -translate-y-0.5" : "opacity-80 hover:opacity-100"}`}
                              >
                                <span className={`inline-block h-1.5 w-1.5 rounded-full mr-2 align-middle ${meta.dot}`} />
                                {o.label}
                                <span className="ml-2 font-mono-num text-[11px] opacity-80">({o.value})</span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60">
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Rest Periods</div>
              <div className="font-heading text-lg font-semibold">{histPrefix ? "Add periods of rest for this day" : "Add periods of rest for today"}</div>
              {histPrefix && <div className="text-[11px] uppercase tracking-widest text-amber-400 font-mono-num mt-0.5">{histPrefix}</div>}
              <p className="text-xs text-slate-500 mt-1">Times snap to 5-minute intervals (00, 05, 10…).</p>
            </div>
            {restPeriods.length === 0 && <div className="text-sm text-slate-500 mb-3">No rest periods logged.</div>}
            <div className="space-y-3">
              {restPeriods.map((r, i) => {
                const hours = r.start_time && r.stop_time
                  ? (new Date(r.stop_time) - new Date(r.start_time)) / 3600000
                  : 0;
                return (
                  <div key={r._key || i} className="p-4 rounded-md border border-slate-800 bg-slate-950/40" data-testid={`rest-${i}`}>
                    <div className="space-y-3">
                      <LocalDateTimeField label="Start" testId={`rest-${i}-start`} value={r.start_time} onChange={(v) => updRest(i, { start_time: v })} disableFuture />
                      <LocalDateTimeField label="Stop" testId={`rest-${i}-stop`} value={r.stop_time} onChange={(v) => updRest(i, { stop_time: v })} disableFuture />
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1"><Clock className="inline h-3 w-3 mr-1" />Total (hrs:min)</label>
                        <div className="font-mono-num text-lg" data-testid={`rest-${i}-total`}>{fmtHM(hours)}</div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <OptField label={qlabels.rest_location || "Location"} testId={`rest-${i}-location`} value={r.location_option_id} onChange={(v) => updRest(i, { location_option_id: v })} options={restLocOpts} />
                    </div>
                    <div className="mt-3">
                      <OptField label="Quality" testId={`rest-${i}-quality`} value={r.quality_option_id} onChange={(v) => updRest(i, { quality_option_id: v })} options={restQualOpts} />
                    </div>
                    <button onClick={() => delRest(i)} data-testid={`rest-${i}-delete`}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-rose-400 hover:text-rose-300">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                );
              })}
            </div>
            <button onClick={addRest} data-testid="add-rest-btn"
              className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
              <Plus className="h-4 w-4" /> Add Rest
            </button>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-6 space-y-4">
            <ScoreGauge score={preview.total} rag={preview.rag} questionsAnswered={answered} totalQuestions={visibleDaily.length} />
            <button disabled={!canSubmit || submitting} onClick={submit} data-testid="submit-entry-btn"
              className="w-full py-3 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? "Submitting…" : activeEditId ? "Save Changes" : "Submit Fatigue Entry"}
            </button>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Score updates live as you tap options. Each option displays its assigned RAG color and numeric weight.
            </p>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function CompletedView({ entry, qlabels, canEdit, onEdit, items }) {
  const byQ = {};
  (entry.breakdown || []).forEach((b) => { byQ[b.question] = b; });
  const list = (items && items.length ? items : DAILY_QS.map((q) => ({ id: q.key, kind: "question", key: q.key, label: q.label })));
  const shown = new Set();
  const qCount = list.filter((i) => i.kind === "question").length;
  const qCard = (key, b) => {
    const meta = RAG_META[b.rag] || RAG_META.green;
    return (
      <div key={key} className="p-4 rounded-lg border border-slate-800 bg-slate-900/60 flex items-center justify-between gap-3 flex-wrap"
        data-testid={`completed-q-${key}`}>
        <div className="text-sm text-slate-300">{qlabels[key] || key}</div>
        <div className={`px-3 py-1.5 rounded-md text-xs ${meta.cls}`}>
          <span className={`inline-block h-1.5 w-1.5 rounded-full mr-2 align-middle ${meta.dot}`} />
          {b.label} <span className="font-mono-num opacity-80">({b.value})</span>
        </div>
      </div>
    );
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-testid="completed-entry-view">
      <div className="lg:col-span-8 space-y-4">
        {list.map((it) => {
          if (it.kind === "divider") {
            return it.label ? (
              <div key={it.id} className="flex items-center gap-3 pt-1" data-testid={`completed-divider-${it.id}`}>
                <div className="h-px flex-1 bg-slate-700" />
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">{it.label}</div>
                <div className="h-px flex-1 bg-slate-700" />
              </div>
            ) : null;
          }
          const b = byQ[it.key];
          if (!b) return null;
          shown.add(it.key);
          return qCard(it.key, b);
        })}
        {(entry.breakdown || []).filter((b) => !shown.has(b.question)).map((b) => qCard(b.question, b))}
        <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/60">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Rest Periods</div>
          {(entry.rest_periods || []).length === 0 && <div className="text-sm text-slate-500">None logged.</div>}
          {(entry.rest_periods || []).map((r) => (
            <div key={r.id} className="text-xs font-mono-num text-slate-300 flex flex-wrap gap-x-4 py-0.5">
              <span>{fmtDT(r.start_time)} → {fmtDT(r.stop_time)}</span>
              <span className="text-slate-400">{fmtHM(r.total_hours)} hrs:min</span>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-6 space-y-4">
          <ScoreGauge score={entry.score_total} rag={entry.rag} questionsAnswered={(entry.breakdown || []).length} totalQuestions={qCount || DAILY_QS.length} />
          {canEdit && (
            <button onClick={onEdit} data-testid="edit-completed-btn"
              className="w-full py-3 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors">
              Edit this entry
            </button>
          )}
          {!canEdit && <p className="text-[11px] text-slate-500">Entries can be edited within 7 days of completion.</p>}
        </div>
      </div>
    </div>
  );
}

function OptField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full px-2 py-2 rounded-md bg-slate-900 border border-slate-800 text-sm focus:border-blue-500 outline-none">
        <option value="">Select…</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.label} ({o.value})</option>)}
      </select>
    </div>
  );
}
